// External modules
import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@emotion/react';
import { XoOnboardingPasskey } from '@paypalcorp/identitywebcomponents';
// @ts-expect-error TODO: @checkout/react-components
import { PayPalLogoPP3 } from '@checkout/react-components';

// Internal modules
import { appStore, useStoreState } from '@/store';
import type { Merchant } from '@/generated/graphql';
import { FONT_SIZE_REGULAR, getTheme } from '@/styles';
import {
  CI,
  getCheckoutSession,
  getContent,
  useEndAndStartCi,
  useMountLogging,
} from '@/lib';
import {
  CenteredContainer,
  Container,
  Footer,
  Header,
  IWCContainer,
  IWCPlaceholder,
  SubTitle,
  Title,
  TitleContainer,
} from './StyledComponents';
import { Layout } from '@/components/Layout';
import { Link, PageLevelAlert } from '@/components/Common';
import { PurchaseCompleteIcon } from './icons';
import type { AuthEvent } from './authEventHandler';
import { authEventHandler } from './authEventHandler';
import { returnToMerchantClickHandler } from './returnToMerchantUtils';
import * as fpti from './fpti';
import { SpinnerRender } from '@/components/Common/Spinner/SpinnerRender';
import { logger } from '@/lib';

export const PasskeyEnrollmentPage = (): JSX.Element => {
  useEndAndStartCi({
    end: CI.Event.APP_ROUTING,
    endStatus: CI.Status.Ok,
    start: CI.Event.PASSKEY_PAGE_AFTER_PAYMENT_COMPLETE,
  });
  useMountLogging('PasskeyEnrollmentPage');

  const { ui: uiActions } = appStore.getActions();

  useEffect(() => {
    uiActions.toggleIsFormSubmitting(false);
    fpti.pageShown();
    logger.metric('GUEST_SIGNUP.PASSKEY.START');
    logger.info('passkey_enrollment_page_rendered');
  }, []);

  const [passkeyError, setPasskeyError] = useState(false);
  const publicCredential = useStoreState(({ form }) => form.email?.value);
  const passkeyModuleOptions = {
    publicCredential,
  };

  const passkeyIWCContext = useStoreState(({ user }) => user.passkey.context);
  const isPasswordlessFlow = useStoreState(
    ({ user }) => user.passkey.isPasswordlessFlow,
  );
  const returnToMerchantUrl = useStoreState(
    ({ user }) => user.passkey.returnToMerchantUrl,
  );
  const merchant = getCheckoutSession().merchant as Merchant;
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);

  window.addEventListener('beforeunload', event => {
    logger.info('passkey_enrollment_page_beforeunload_triggered');
    fpti.passkeyBeforeUnload();
    logger.flush();
  });

  // This is purely for debugging purposes. Can be removed at later point
  useEffect(() => {
    if (passkeyIWCContext) {
      logger.info('passkey_enrollment_page_display_iwc_triggered', {
        passkeyIWCContext,
      });
      // This logs says that we have triggered IWC component render
      // and we need to track on any rendering issues on IWC after this
      // NOTE: This also says that passkey IWC context was present while
      // rendering passkey enrollment page
      fpti.passkeyDisplayIWCTriggered();
    } else {
      logger.error('passkey_enrollment_page_missing_iwc_context', {
        passkeyIWCContext,
      });
    }
  }, [passkeyIWCContext]);

  const dispatchAuthEvent = (event: AuthEvent) => {
    authEventHandler(event, {
      isPasswordlessFlow,
      returnToMerchantUrl,
      setPasskeyError,
    });
    // Flush logs upon every auth event fired
    // NOTE: This is needed to make sure we match the log count between
    // Checkout and UL/IWC. If we don't flush the log and if the page is
    // closed before logs are sent to backend, then there will be a mismatch
    // in the funnel count of checkout and UL/IWC
    logger.flush();
  };

  const dispatchReturnToMerchantEvent = (event: React.MouseEvent): void => {
    event.preventDefault();

    logger.info('passkey_back_to_merchant_clicked');

    // TODO: add additional FPTI log for this
    logger.fpti('passkey_back_to_merchant_clicked', {
      event_name: 'buttonClicked',
      state_name: 'passkey_back_to_merchant',
      transition_name: 'process_passkey_back_to_merchant',
    });
    returnToMerchantClickHandler(event, {
      isPasswordlessFlow,
      returnToMerchantUrl,
    });
  };

  return (
    <Layout
      isContentFullWidth={true}
      pageName="passkey"
      showHeader={false}
      showFooter={false}
      showValueProps={false}
    >
      <ThemeProvider theme={getTheme('ppReactV7')}>
        <Container data-testid="passkey-enrollment-page">
          <Header>
            <PayPalLogoPP3 />
          </Header>
          <CenteredContainer>
            {passkeyError && (
              <PageLevelAlert
                alertContent={getContent('passkey.failed')}
                icon="INFO_SPOT_V2"
                pageLevelAlertVariant="warning"
              />
            )}
            <PurchaseCompleteIcon />
            <TitleContainer data-testid="purchase-complete-title">
              <Title>{getContent('passkey.purchaseComplete')}</Title>
              <SubTitle>{getContent('passkey.thanksForUsingPaypal')}</SubTitle>
            </TitleContainer>
            <IWCPlaceholder>
              {passkeyIWCContext && (
                <IWCContainer>
                  <XoOnboardingPasskey
                    context={passkeyIWCContext}
                    options={passkeyModuleOptions}
                    onAuthEvent={dispatchAuthEvent}
                  />
                </IWCContainer>
              )}
            </IWCPlaceholder>
          </CenteredContainer>
          <Footer>
            <Link
              fontSize={FONT_SIZE_REGULAR}
              href="#"
              onClick={dispatchReturnToMerchantEvent}
              testId="passkey-back-to-merchant"
              text={getContent('passkey.backToMerchant', {
                merchantName: merchant.name ?? getContent('base.theMerchant'),
              })}
            />
          </Footer>
        </Container>
        {isFormSubmitting && (
          <SpinnerRender
            containToParent={false}
            overlayStyleOverrides={{
              backgroundColor: '#fff',
              opacity: 0.9,
              zIndex: 1089,
            }}
          />
        )}
      </ThemeProvider>
    </Layout>
  );
};
